import React, { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useSystem } from '@contexts/SystemContext'
import { notificationService } from '@services/notificationService'
import NotificationDropdown from '@components/Notifications/NotificationDropdown'
import TimerHeaderWidget from '@components/Timer/TimerHeaderWidget'
import { 
  Clock, 
  FolderOpen, 
  FileText, 
  BarChart3, 
  Settings, 
  Users,
  Bell,
  User,
  LogOut,
  CheckSquare,
  Menu,
  Map
} from 'lucide-react'

interface LayoutProps {
  children: ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const { user, logout } = useAuth()
  const { systemSettings } = useSystem()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [notificationCount, setNotificationCount] = useState(0)
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false)

  // 獲取軟體名稱，如果還沒載入則使用預設值
  const softwareName = systemSettings?.softwareName || 'AnyTime'

  const navigationItems = [
    { path: '/timer', label: '計時器', icon: Clock },
    // { path: '/partner-map', label: '夥伴地圖', icon: Map },
    ...(user?.role === 'ADMIN' || user?.role === 'MANAGER' ? [{ path: '/projects', label: '專案管理', icon: FolderOpen }] : []),
    { path: '/timesheets', label: '工時申報', icon: FileText },
    ...(user?.role === 'ADMIN' || user?.role === 'MANAGER' ? [{ path: '/approvals', label: '工時審核', icon: CheckSquare }] : []),
    { path: '/reports', label: '報表分析', icon: BarChart3 },
    ...(user?.role === 'ADMIN' ? [{ path: '/users', label: '用戶管理', icon: Users }] : []),
    { path: '/settings', label: '設定', icon: Settings },
  ]

  const isActivePath = (path: string) => {
    if (path === '/timer') {
      return location.pathname === '/' || location.pathname.startsWith('/timer')
    }
    return location.pathname.startsWith(path)
  }

  const handleLogout = () => {
    logout()
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const closeSidebar = () => {
    setIsSidebarOpen(false)
  }

  const handleNavClick = () => {
    // 在手機版點擊導航後關閉側邊欄
    if (window.innerWidth < 768) {
      closeSidebar()
    }
  }

  // 載入通知數量
  useEffect(() => {
    const loadNotificationCount = async () => {
      try {
        const count = await notificationService.getUnreadCount()
        setNotificationCount(count)
      } catch (error) {
        console.error('Error loading notification count:', error)
      }
    }

    if (user) {
      loadNotificationCount()
      // 設定定時器每分鐘更新一次通知數量
      const interval = setInterval(loadNotificationCount, 60000)
      return () => clearInterval(interval)
    }
  }, [user])

  // 監聽螢幕尺寸變化
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleNotificationDropdown = () => {
    setShowNotificationDropdown(!showNotificationDropdown)
  }

  const closeNotificationDropdown = () => {
    setShowNotificationDropdown(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      {/* 手機版頂部 Header */}
      <header className="md:hidden bg-white shadow-soft border-b border-primary-200 px-4 py-4 fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleSidebar}
              className="btn-ghost p-2"
              aria-label="開啟選單"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="block">
              <h1 className="text-xl font-bold text-gradient">{softwareName}</h1>
            </Link>
            {/* Mobile Timer Widget */}
            <TimerHeaderWidget />
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button 
                className="btn-ghost relative p-2"
                onClick={toggleNotificationDropdown}
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
              {showNotificationDropdown && (
                <NotificationDropdown 
                  onClose={closeNotificationDropdown} 
                  onUnreadCountChange={setNotificationCount}
                />
              )}
            </div>
            <div className="relative group">
              <button className="btn-ghost flex items-center space-x-2 p-2">
                <User className="w-5 h-5" />
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-primary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 hover:bg-primary-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>個人設定</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>登出</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 手機版背景遮罩 */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden"
          onClick={closeSidebar}
        />
      )}

      <div className="flex md:pt-0 pt-20">
        {/* 側邊欄 */}
        <aside className={`
          fixed md:relative top-0 left-0 h-full w-64 bg-white shadow-soft border-r border-primary-200 z-40
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:min-h-screen
        `}>
          <div className="p-6">
            <Link to="/" className="block">
              <h1 className="text-2xl font-bold text-gradient">{softwareName}</h1>
              <p className="text-sm text-secondary-600 mt-1">計時管理系統</p>
            </Link>
          </div>
          
          <nav className="px-4 space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = isActivePath(item.path)
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive ? 'nav-link-active' : ''} space-x-2`}
                  onClick={handleNavClick}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {/* Header - 只在桌面版顯示 */}
          <header className="hidden md:block bg-white shadow-soft border-b border-primary-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {/* Timer Header Widget */}
                <TimerHeaderWidget />
              </div>
              <div className="flex items-center space-x-4">
                {/* Quick Timer Button */}
                <Link
                  to="/timer"
                  className="btn-primary"
                >
                  開始計時
                </Link>
                
                {/* View Reports Button */}
                <Link
                  to="/reports"
                  className="btn-ghost"
                >
                  查看報表
                </Link>
                
                {/* Notifications */}
                <div className="relative">
                  <button 
                    className="btn-ghost relative"
                    onClick={toggleNotificationDropdown}
                  >
                    <Bell className="w-5 h-5" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {notificationCount > 99 ? '99+' : notificationCount}
                      </span>
                    )}
                  </button>
                  {showNotificationDropdown && (
                    <NotificationDropdown 
                      onClose={closeNotificationDropdown}
                      onUnreadCountChange={setNotificationCount}
                    />
                  )}
                </div>
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="btn-ghost flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>{user?.name || '用戶'}</span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-primary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      <Link
                        to="/settings"
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 hover:bg-primary-50 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>個人設定</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>登出</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
